import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { UserPlus, UserCheck, Search, Filter, Shield, Clock, XCircle } from 'lucide-react';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import styles from './Visitors.module.css';

const getImageUrl = (path) => {
  if (!path) return '';
  const baseUrl = import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '');
  return `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
};

const Visitors = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCamera, setShowCamera] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);

  useEffect(() => {
    fetchVisitors();
  }, []);

  const fetchVisitors = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/security/visitors');
      setVisitors(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState({ name: '', phone: '', flatNumber: '', purpose: 'Guest' });
  const [photoFile, setPhotoFile] = useState(null);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setPhotoFile(e.target.files[0]);
    }
  };

  const startCamera = async () => {
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      toast.error("Camera access denied or not available");
      setShowCamera(false);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], "visitor_photo.jpg", { type: "image/jpeg" });
        setPhotoFile(file);
        stopCamera();
      }, 'image/jpeg', 0.8);
    }
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    stopCamera();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('phone', formData.phone);
      data.append('flatNumber', formData.flatNumber);
      data.append('purpose', formData.purpose);
      if (photoFile) {
        data.append('photo', photoFile);
      }

      const res = await api.post('/security/visitors', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setVisitors([res.data, ...visitors]);
      closeDrawer();
      setFormData({ name: '', phone: '', flatNumber: '', purpose: 'Guest' });
      setPhotoFile(null);
      toast.success('Visitor logged successfully!');
    } catch (err) {
      toast.error('Failed to log visitor');
    }
  };

  const handleAction = async (id, action) => {
    try {
      const { data } = await api.put(`/security/visitors/${id}/status`, { status: action });
      setVisitors(visitors.map(v => v._id === id ? data : v));
      toast.success('Status updated successfully!');
    } catch (err) {
      toast.error('Failed to update status');
    }
  };
  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div>
          <h1>Visitor Log</h1>
          <p>Register new visitors and monitor entries/exits</p>
        </div>
        <button className={styles.addBtn} onClick={() => setIsDrawerOpen(true)}>
          <UserPlus size={16} /> New Entry
        </button>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.searchBox}>
            <Search size={16} className={styles.searchIcon} />
            <input type="text" placeholder="Search by name, flat or phone..." />
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Visitor Info</th>
                <th>Flat No.</th>
                <th>Purpose</th>
                <th>In Time</th>
                <th>Out Time</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="7">Loading...</td></tr> : visitors.map(v => (
                <tr key={v._id}>
                  <td>
                    <div className={styles.visitorInfo}>
                      <div className={styles.avatarSmall}>
                        {v.photo ? <img src={getImageUrl(v.photo)} onClick={(e) => { e.stopPropagation(); setSelectedImage(v.photo); }} alt="Visitor" style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', cursor: 'pointer'}} /> : v.name.charAt(0)}
                      </div>
                      <div className={styles.details}>
                        <strong>{v.name}</strong>
                        <span>{v.phone}</span>
                      </div>
                    </div>
                  </td>
                  <td><strong>{v.flatNumber || '-'}</strong></td>
                  <td><span className={styles.badgePurpose}>{v.purpose}</span></td>
                  <td>{v.entryTime ? new Date(v.entryTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : (v.status !== 'Expected' && v.createdAt ? new Date(v.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-')}</td>
                  <td>{v.exitTime ? new Date(v.exitTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ((v.status === 'Completed' || v.status === 'Denied') && v.updatedAt ? new Date(v.updatedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-')}</td>
                  <td>
                    <span className={`${styles.badgeStatus} ${styles[v.status.toLowerCase()]}`}>
                      {v.status}
                    </span>
                  </td>
                  <td>
                    {(v.status === 'Pending' || v.status === 'Expected') && (
                      <div className={styles.actionBtns}>
                        <button className={styles.btnAllow} onClick={() => handleAction(v._id, 'Allowed')}>
                          <UserCheck size={14} /> {v.status === 'Expected' ? 'Check In' : 'Allow'}
                        </button>
                        <button className={styles.btnDeny} onClick={() => handleAction(v._id, 'Denied')}>
                          <XCircle size={14} /> Deny
                        </button>
                      </div>
                    )}
                    {v.status === 'Allowed' && (
                      <button className={styles.btnExit} onClick={() => handleAction(v._id, 'Completed')}>
                        Mark Exit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile View */}
        <div className={styles.mobileList}>
          {loading ? <p style={{padding: '16px'}}>Loading...</p> : visitors.map(v => (
            <div key={v._id} className={styles.mobileCard}>
              <div className={styles.mobileCardHeader}>
                <div className={styles.visitorInfo}>
                  <div className={styles.avatarSmall}>
                    {v.photo ? <img src={getImageUrl(v.photo)} onClick={(e) => { e.stopPropagation(); setSelectedImage(v.photo); }} alt="Visitor" style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', cursor: 'pointer'}} /> : v.name.charAt(0)}
                  </div>
                  <div className={styles.details}>
                    <strong>{v.name}</strong>
                    <span>{v.phone}</span>
                  </div>
                </div>
                <span className={`${styles.badgeStatus} ${styles[v.status.toLowerCase()]}`}>
                  {v.status}
                </span>
              </div>
              <div className={styles.mobileCardMeta}>
                <div className={styles.metaRow}>
                  <span>Flat: <strong>{v.flatNumber || '-'}</strong></span>
                  <span className={styles.badgePurpose}>{v.purpose}</span>
                </div>
                <div className={styles.metaRow}>
                  <span>In: {v.entryTime ? new Date(v.entryTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : (v.status !== 'Expected' && v.createdAt ? new Date(v.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-')}</span>
                  <span>Out: {v.exitTime ? new Date(v.exitTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ((v.status === 'Completed' || v.status === 'Denied') && v.updatedAt ? new Date(v.updatedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-')}</span>
                </div>
                <div className={styles.metaRow}>
                    {(v.status === 'Pending' || v.status === 'Expected') && (
                      <div className={styles.actionBtns} style={{flexDirection: 'row', width: '100%'}}>
                        <button className={styles.btnAllow} style={{flex: 1, justifyContent: 'center'}} onClick={() => handleAction(v._id, 'Allowed')}>
                          <UserCheck size={14} /> {v.status === 'Expected' ? 'Check In' : 'Allow'}
                        </button>
                        <button className={styles.btnDeny} style={{flex: 1, justifyContent: 'center'}} onClick={() => handleAction(v._id, 'Denied')}>
                          <XCircle size={14} /> Deny
                        </button>
                      </div>
                    )}
                    {v.status === 'Allowed' && (
                      <button className={styles.btnExit} style={{width: '100%', justifyContent: 'center'}} onClick={() => handleAction(v._id, 'Completed')}>
                        Mark Exit
                      </button>
                    )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Drawer isOpen={isDrawerOpen} onClose={closeDrawer} title="New Visitor Entry">
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <label>Visitor Name</label>
            <input name="name" value={formData.name} onChange={handleChange} required placeholder="Full Name" />
          </div>
          <div className="formGroup">
            <label>Phone Number</label>
            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="10-digit number" />
          </div>
          <div className="formGroup">
            <label>Visiting Flat</label>
            <input name="flatNumber" value={formData.flatNumber} onChange={handleChange} required placeholder="e.g. A-101" />
          </div>
          <div className="formGroup">
            <label>Purpose</label>
            <select name="purpose" value={formData.purpose} onChange={handleChange}>
              <option value="Guest">Guest / Relative</option>
              <option value="Delivery">Delivery</option>
              <option value="Maintenance">Maintenance / Service</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="formGroup">
            <label>Visitor Photo (Optional)</label>
            {!showCamera ? (
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {photoFile ? (
                  <div style={{ position: 'relative', width: '80px', height: '80px' }}>
                    <img src={URL.createObjectURL(photoFile)} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                    <button type="button" onClick={() => setPhotoFile(null)} style={{ position: 'absolute', top: '-5px', right: '-5px', background: 'red', color: 'white', borderRadius: '50%', width: '20px', height: '20px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                  </div>
                ) : (
                  <button type="button" onClick={startCamera} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#e2e8f0', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer' }}>
                    📷 Take Photo
                  </button>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center', background: '#000', padding: '10px', borderRadius: '8px' }}>
                <video ref={videoRef} autoPlay playsInline style={{ width: '100%', maxHeight: '300px', borderRadius: '4px' }}></video>
                <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" onClick={capturePhoto} style={{ padding: '8px 16px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Capture</button>
                  <button type="button" onClick={stopCamera} style={{ padding: '8px 16px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={closeDrawer}>Cancel</button>
            <button type="submit" className="btnSubmit">Log Entry</button>
          </div>
        </form>
      </Drawer>

      {selectedImage && (
        <div 
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}
          onClick={() => setSelectedImage(null)}
        >
          <img src={getImageUrl(selectedImage)} style={{ maxWidth: '90%', maxHeight: '90%', borderRadius: '8px' }} alt="Enlarged Visitor" />
          <button 
            onClick={() => setSelectedImage(null)}
            style={{ position: 'absolute', top: '20px', right: '20px', background: 'transparent', color: 'white', border: 'none', fontSize: '40px', cursor: 'pointer' }}
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
};

export default Visitors;
